import { z } from 'zod';
import { QualitySchema, SiteStateAlertSchema, SiteStateDeltaSchema } from './ingest.js';

/**
 * Live push contract for the per-site `SiteState` Durable Object (CLAUDE.md §7.3, §11.3).
 *
 * Two surfaces share these shapes:
 *  - `POST /forward` (cloud-internal): the queue consumer pushes shaped deltas + alerts into
 *    the DO. Body = `SiteStateForward` (frozen since M03 — the consumer already posts it).
 *  - `GET /live` (WebSocket): the DO fans out `LiveMessage`s to subscribed dashboards. The
 *    first frame is always a `snapshot`; subsequent frames are `delta` / `alert`.
 *
 * Demand (MW) is derived by the DO from the configured incomer tag (see
 * packages/config/metrics.ts `DEMAND_TAG`); kW values are divided by 1000.
 */

/** Latest value for one tag, as held in DO live state. */
export const LiveTagValueSchema = z.object({
  tag: z.string(),
  value: z.number().nullable(),
  quality: QualitySchema,
  ts: z.string().datetime(),
});
export type LiveTagValue = z.infer<typeof LiveTagValueSchema>;

/** One point in the DO's rolling last-hour demand window (MW). */
export const LiveDemandPointSchema = z.object({
  ts: z.string().datetime(),
  mw: z.number(),
});
export type LiveDemandPoint = z.infer<typeof LiveDemandPointSchema>;

/** `POST /forward` body — the consumer→DO contract (frozen since M03).
 * `SiteStateDeltaSchema` / `SiteStateAlertSchema` are re-used from ./ingest.ts (single
 * source of truth — the consumer shapes them there). */
export const SiteStateForwardSchema = z.object({
  siteId: z.string(),
  deltas: z.array(SiteStateDeltaSchema),
  alerts: z.array(SiteStateAlertSchema),
});
export type SiteStateForward = z.infer<typeof SiteStateForwardSchema>;

// ── WebSocket fan-out frames ──────────────────────────────────────────────────

/** First frame on connect: a full snapshot of current live state. */
export const LiveSnapshotSchema = z.object({
  type: z.literal('snapshot'),
  siteId: z.string(),
  generatedAt: z.string().datetime(),
  /** Current site demand (MW), or null if the incomer tag has not reported. */
  demandMw: z.number().nullable(),
  tags: z.array(LiveTagValueSchema),
  /** Rolling last-hour demand window (MW). */
  window: z.array(LiveDemandPointSchema),
  openAlerts: z.array(SiteStateAlertSchema),
});
export type LiveSnapshot = z.infer<typeof LiveSnapshotSchema>;

/** A tag value changed. */
export const LiveDeltaMessageSchema = z.object({
  type: z.literal('delta'),
  tag: z.string(),
  value: z.number().nullable(),
  quality: QualitySchema,
  ts: z.string().datetime(),
  /** Current site demand (MW) after applying this delta, if it moved demand. */
  demandMw: z.number().nullable(),
});
export type LiveDeltaMessage = z.infer<typeof LiveDeltaMessageSchema>;

/** A new alert was raised. */
export const LiveAlertMessageSchema = z.object({
  type: z.literal('alert'),
  alert: SiteStateAlertSchema,
});
export type LiveAlertMessage = z.infer<typeof LiveAlertMessageSchema>;

export const LiveMessageSchema = z.discriminatedUnion('type', [
  LiveSnapshotSchema,
  LiveDeltaMessageSchema,
  LiveAlertMessageSchema,
]);
export type LiveMessage = z.infer<typeof LiveMessageSchema>;
